import { useEffect, useState } from 'react';
import { CheckCircle2, Fingerprint, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type {
  ValidationEvidenceInput,
  ValidationEvidenceResult,
  ValidationEvidenceType,
  ValidationRequirement,
} from '../../types/validation.types';

function localDateTimeValue() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

interface ValidationEvidenceDialogProps {
  requirement: ValidationRequirement | null;
  defaultUsername: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: ValidationEvidenceInput) => Promise<void>;
}

export function ValidationEvidenceDialog({
  requirement,
  defaultUsername,
  isPending,
  onOpenChange,
  onConfirm,
}: ValidationEvidenceDialogProps) {
  const [evidenceType, setEvidenceType] = useState<ValidationEvidenceType>('automated_test');
  const [result, setResult] = useState<ValidationEvidenceResult>('pass');
  const [reference, setReference] = useState('');
  const [objectiveNotes, setObjectiveNotes] = useState('');
  const [executedAt, setExecutedAt] = useState(localDateTimeValue);
  const [reason, setReason] = useState('');
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!requirement) return;
    setEvidenceType('automated_test');
    setResult('pass');
    setReference('');
    setObjectiveNotes('');
    setExecutedAt(localDateTimeValue());
    setReason('');
    setUsername(defaultUsername);
    setPassword('');
  }, [defaultUsername, requirement]);

  const canSubmit = Boolean(
    requirement
      && reference.trim().length >= 3
      && objectiveNotes.trim().length >= 3
      && Boolean(executedAt)
      && !Number.isNaN(new Date(executedAt).getTime())
      && reason.trim().length >= 3
      && username.trim()
      && password,
  );

  return (
    <Dialog open={Boolean(requirement)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-50 text-cyan-800">
            <Fingerprint className="h-5 w-5" />
          </div>
          <DialogTitle>Record objective evidence</DialogTitle>
          <DialogDescription>
            {requirement?.requirementCode} · {requirement?.statement}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="validation-evidence-type">Evidence method</Label>
              <Select
                value={evidenceType}
                onValueChange={(value) => setEvidenceType(value as ValidationEvidenceType)}
              >
                <SelectTrigger id="validation-evidence-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="automated_test">Automated test</SelectItem>
                  <SelectItem value="manual_test">Manual test</SelectItem>
                  <SelectItem value="document_review">Document review</SelectItem>
                  <SelectItem value="data_review">Data review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="validation-result">Objective result</Label>
              <Select
                value={result}
                onValueChange={(value) => setResult(value as ValidationEvidenceResult)}
              >
                <SelectTrigger id="validation-result">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass · control verified</SelectItem>
                  <SelectItem value="fail">Fail · gap recorded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={`rounded-lg border px-3 py-2 text-sm ${
            result === 'pass'
              ? 'border-teal-200 bg-teal-50 text-teal-950'
              : 'border-amber-200 bg-amber-50 text-amber-950'
          }`}>
            <div className="flex items-center gap-2 font-medium">
              {result === 'pass'
                ? <CheckCircle2 className="h-4 w-4" />
                : <ShieldAlert className="h-4 w-4" />}
              {result === 'pass'
                ? 'This signature will mark the requirement Verified.'
                : 'This signature will preserve the failed result as an open Gap.'}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="validation-reference">Evidence reference</Label>
            <Input
              id="validation-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Test run, controlled document, report, or source record ID"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="validation-notes">Objective observations</Label>
            <Textarea
              id="validation-notes"
              value={objectiveNotes}
              onChange={(event) => setObjectiveNotes(event.target.value)}
              placeholder="Record what was observed, including any exceptions."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="validation-executed-at">Executed at</Label>
            <Input
              id="validation-executed-at"
              type="datetime-local"
              value={executedAt}
              onChange={(event) => setExecutedAt(event.target.value)}
            />
            <p className="text-xs text-slate-500">
              The server stores and manifests this time in UTC.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="mb-3 text-sm font-semibold text-slate-900">
              Electronic verification signature
            </div>
            <div className="space-y-2">
              <Label htmlFor="validation-signature-reason">Signature reason</Label>
              <Textarea
                id="validation-signature-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="I verified this result against the stated acceptance criteria."
                rows={2}
              />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="validation-signature-username">Username</Label>
                <Input
                  id="validation-signature-username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validation-signature-password">Password</Label>
                <Input
                  id="validation-signature-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-cyan-800 text-white hover:bg-cyan-900"
            disabled={!canSubmit || isPending}
            onClick={() => onConfirm({
              evidenceType,
              result,
              reference: reference.trim(),
              objectiveNotes: objectiveNotes.trim(),
              executedAt: new Date(executedAt).toISOString(),
              meaning: 'verification',
              reason: reason.trim(),
              username: username.trim(),
              password,
            })}
          >
            {isPending ? 'Signing…' : 'Sign verification'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
